import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { ActivatedRoute, Router } from "@angular/router";
import { DateSelectionService } from "../services/date-selection.service";
import { MealService } from "../core/services/meal.service";
import { UserService } from "../core/services/user.service";
import { LanguageService } from "../core/services/language.service";
import { getMealData as getMealDataCommon, generateDateOptions, handleBackNavigation } from "../shared/utils/commenFunctions";
import { Subscription } from "rxjs";
import { pageLoadAnimationBottomToCenter } from "../shared/utils/common-animations";
import { getUserDetails, getProgramId, getSelectedLocation } from "../shared/utils/storage-utils";

type MealOptionState = "Hot" | "Cold" | "No Meal";
type DrinkOptionState = "Milk" | "Chocolate Milk" | "Orange Juice";
type TransportOptionState = "To Center" | "To Home" | "Round Trip" | "Not Required";
type SectionKey = "meal" | "drink" | "transport";

interface MealOption {
  id: MealOptionState;
  label: string;
  image: string;
  dietIcon?: string;
  dietLabel?: string;
  waitlistCount: number;
}

interface DrinkOption {
  id: DrinkOptionState;
  label: string;
  image: string;
  waitlistCount: number;
}

interface TransportOption {
  id: TransportOptionState;
  label: string;
  image: string;
  waitlistCount: number;
}

type Option = MealOption | DrinkOption | TransportOption;

interface OptionSection {
  key: SectionKey;
  title: string;
  options: Option[];
}

@Component({
  selector: "meal-preferance-component, MealPreferanceComponent",
  standalone: true,
  templateUrl: "./meal-preferance.component.html",
  styleUrls: ["./meal-preferance.component.css"],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimationBottomToCenter]
})
export class MealPreferanceComponent implements OnInit, OnDestroy {
  private routeSubscription?: Subscription;
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dateSelectionService: DateSelectionService,
    private readonly mealService: MealService,
    private readonly userService: UserService,
    private readonly location: Location
  ) {}
  pageLoadState = "hidden";
  date: string | null = null;
  dateLabel: string = '';
  mealLocationName: string = '';
  userCount: any = null;
  readonly mealOptions: MealOption[] = [
    {
      id: "Hot",
      label: "Hot",
      image: "assets/images/meal-preference/hot.png",
      waitlistCount: 0,
    },
    {
      id: "Cold",
      label: "Cold",
      image: "assets/images/meal-preference/cold.png",
      waitlistCount: 0,
    },
    {
      id: "No Meal",
      label: "No Meal",
      image: "assets/images/meal-preference/no-meal.png",
      waitlistCount: 0,
    },
  ];

  readonly drinkOptions: DrinkOption[] = [
    {
      id: "Milk",
      label: "Milk",
      image: "assets/images/meal-preference/milk.png",
      waitlistCount: 0,
    },
    {
      id: "Chocolate Milk",
      label: "Chocolate Milk",
      image: "assets/images/meal-preference/chocolate-milk.png",
      waitlistCount: 0,
    },
    {
      id: "Orange Juice",
      label: "Orange Juice",
      image: "assets/images/meal-preference/orange-juice.png",
      waitlistCount: 0,
    },
  ];

  readonly transportOptions: TransportOption[] = [
    {
      id: "Round Trip",
      label: "Round Trip",
      image: "assets/images/meal-preference/round-trip.png",
      waitlistCount: 0,
    },
    {
      id: "To Center",
      label: "To Center",
      image: "assets/images/meal-preference/to-center.png",
      waitlistCount: 0,
    },
    {
      id: "To Home",
      label: "To Home",
      image: "assets/images/meal-preference/to-home.png",
      waitlistCount: 0,
    },
    {
      id: "Not Required",
      label: "Not Required",
      image: "assets/images/meal-preference/not-required.png",
      waitlistCount: 0,
    },
  ];

  mealSelection: MealOptionState | null = null;
  drinkSelection: DrinkOptionState | null = null;
  transportSelection: TransportOptionState | null = null;
  /** True when this date already had a saved reservation (edit) — clearing selections cancels it. */
  hadExistingReservation = false;
  userDetails:any = getUserDetails();
  mealData: any = null;
  /** From meals/get — special-activity special menu for this date. */
  offerHotMeal = true;
  offerColdMeal = true;
  /** Senior Center: transport UI only when transportation is approved. Start hidden for program 1000 until meals/get confirms. */
  isTransportationApproved = getProgramId() !== 1000;
  /** Approved application transportation direction (Round Trip / To Center / To Home). */
  approvedTransportationOption: string | null = null;
  toasterMessage: string | null = null;
  toasterVariant: "success" | "danger" | "warning" | "info" = "info";
  /** Stable sections array — do not rebuild on every change detection (breaks clicks). */
  optionSections: OptionSection[] = [];

  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private languageSub?: Subscription;

  dismissToaster(): void {
    this.toasterMessage = null;
    this.toasterVariant = "info";
  }

  get canShowTransport(): boolean {
    const programId = getProgramId();
    if (programId === 1000) {
      return this.isTransportationApproved;
    }
    return true;
  }

  get preferenceTitle(): string {
    if (this.isNoMealSelected) {
      return this.languageService.t('meal.title.noMeal');
    }
    if (this.hasAnySelection()) {
      return this.canShowTransport
        ? this.languageService.t('meal.title.selectedBoth')
        : this.languageService.t('meal.title.selectedMeal');
    }
    return this.canShowTransport
      ? this.languageService.t('meal.title.selectBoth')
      : this.languageService.t('meal.title.selectMeal');
  }

  get waitlistBannerText(): string {
    return this.languageService.t('meal.waitlistBanner');
  }

  get preferredHintText(): string {
    return this.languageService.t('meal.preferredHint');
  }

  get backLabel(): string {
    return this.languageService.t('common.back');
  }

  get saveLabel(): string {
    return this.languageService.t('common.save');
  }

  get applyFutureLabel(): string {
    return this.languageService.t('meal.applyFuture');
  }

  get isNoMealSelected(): boolean {
    return this.mealSelection === 'No Meal';
  }

  private localizeMealOptions(): MealOption[] {
    return this.mealOptions.map((option) => ({
      ...option,
      label:
        option.id === 'Hot'
          ? this.languageService.t('meal.option.hot')
          : option.id === 'Cold'
            ? this.languageService.t('meal.option.cold')
            : this.languageService.t('meal.option.noMeal'),
    }));
  }

  private localizeDrinkOptions(): DrinkOption[] {
    return this.drinkOptions.map((option) => ({
      ...option,
      label:
        option.id === 'Milk'
          ? this.languageService.t('meal.option.milk')
          : option.id === 'Chocolate Milk'
            ? this.languageService.t('meal.option.chocolateMilk')
            : this.languageService.t('meal.option.orangeJuice'),
    }));
  }

  private localizeTransportOptions(): TransportOption[] {
    const allowedIds = this.getAllowedTransportOptionIds();
    return this.transportOptions
      .filter((option) => allowedIds.includes(option.id))
      .map((option) => ({
        ...option,
        label:
          option.id === 'Round Trip'
            ? this.languageService.t('meal.option.roundTrip')
            : option.id === 'To Center'
              ? this.languageService.t('meal.option.toCenter')
              : option.id === 'To Home'
                ? this.languageService.t('meal.option.toHome')
                : this.languageService.t('meal.option.notRequired'),
      }));
  }

  /**
   * Client rule: only show directions that match the approved application option.
   * Round Trip → all options; one-way → that direction + Not Required.
   */
  private getAllowedTransportOptionIds(): TransportOptionState[] {
    const approved = this.normalizeTransportOption(this.approvedTransportationOption);
    if (!approved || approved === 'Not Required') {
      // Unknown / not required approval → keep full set when transport is shown.
      return ['Round Trip', 'To Center', 'To Home', 'Not Required'];
    }
    if (approved === 'Round Trip') {
      return ['Round Trip', 'To Center', 'To Home', 'Not Required'];
    }
    if (approved === 'To Home') {
      return ['To Home', 'Not Required'];
    }
    if (approved === 'To Center') {
      return ['To Center', 'Not Required'];
    }
    return ['Round Trip', 'To Center', 'To Home', 'Not Required'];
  }

  private rebuildOptionSections(): void {
    const localizedMeals = this.localizeMealOptions().filter((option) => {
      if (option.id === 'No Meal') {
        return true;
      }
      if (option.id === 'Hot') {
        return this.offerHotMeal;
      }
      if (option.id === 'Cold') {
        return this.offerColdMeal;
      }
      return true;
    });

    const sections: OptionSection[] = [
      { key: "meal", title: this.languageService.t('meal.section.meal'), options: localizedMeals },
    ];
    // No Meal locks drink + transport (client: no other options including transportation).
    if (!this.isNoMealSelected) {
      sections.push({
        key: "drink",
        title: this.languageService.t('meal.section.drink'),
        options: this.localizeDrinkOptions(),
      });
      if (this.canShowTransport) {
        sections.push({
          key: "transport",
          title: this.languageService.t('meal.section.transport'),
          options: this.localizeTransportOptions(),
        });
      }
    }
    this.optionSections = sections;
  }

  /** Hot/Cold filtered by special-activity special menu for this date; No Meal always available. */
  get visibleMealOptions(): MealOption[] {
    return this.mealOptions.filter((option) => {
      if (option.id === 'No Meal') {
        return true;
      }
      if (option.id === 'Hot') {
        return this.offerHotMeal;
      }
      if (option.id === 'Cold') {
        return this.offerColdMeal;
      }
      return true;
    });
  }

  private applyMealOfferOptions(response: any): void {
    this.offerHotMeal = response?.offerHotMeal !== false;
    this.offerColdMeal = response?.offerColdMeal !== false;
    if (this.mealSelection === 'Hot' && !this.offerHotMeal) {
      this.mealSelection = null;
    }
    if (this.mealSelection === 'Cold' && !this.offerColdMeal) {
      this.mealSelection = null;
    }
  }

  private toggleSelection<T extends string | null>(current: T, optionId: string): T {
    return current === optionId ? null as T : optionId as T;
  }
  countData: any = null;
  mealCountData: any = null; // Store count from meals/get API
  userCountData: any = null; // Store count from users/count API
  displayedCount: number = 0; // Count to display based on selection state
  
  selectMeal(optionId: MealOptionState): void {
    this.mealSelection = this.toggleSelection(this.mealSelection, optionId);
    // Untab meal (or No Meal) clears dependent choices — empty meal = cancel on edit save.
    if (!this.mealSelection || this.mealSelection === 'No Meal') {
      this.drinkSelection = null;
      this.transportSelection = null;
    }
    this.rebuildOptionSections();
    // Update displayed count based on selection (no API call)
    this.updateDisplayedCount();
  }

  selectDrink(optionId: DrinkOptionState): void {
    if (this.isNoMealSelected) {
      return;
    }
    this.drinkSelection = this.toggleSelection(this.drinkSelection, optionId);
    // Update displayed count based on selection (no API call)
    this.updateDisplayedCount();
  }

  selectTransport(optionId: TransportOptionState): void {
    if (this.isNoMealSelected) {
      return;
    }
    this.transportSelection = this.toggleSelection(this.transportSelection, optionId);
    // Update displayed count based on selection (no API call)
    this.updateDisplayedCount();
  }

  private normalizeTransportOption(value: string | null | undefined): TransportOptionState | null {
    const normalized = (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalized) return null;
    if (
      normalized === 'to center and from home' ||
      normalized === 'to centre and from home' ||
      normalized === 'both' ||
      normalized === 'round trip' ||
      normalized === 'roundtrip'
    ) {
      return 'Round Trip';
    }
    if (
      normalized === 'to center' ||
      normalized === 'to centre' ||
      normalized === 'center' ||
      normalized === 'centre' ||
      normalized === 'transportation to center' ||
      normalized === 'transportation to centre'
    ) {
      return 'To Center';
    }
    if (
      normalized === 'from home' ||
      normalized === 'to home' ||
      normalized === 'home' ||
      normalized === 'transportation to home'
    ) {
      return 'To Home';
    }
    if (
      normalized === 'no need' ||
      normalized === 'not required' ||
      normalized === 'notrequired' ||
      normalized === 'none'
    ) {
      return 'Not Required';
    }
    return null;
  }

  selectSectionOption(sectionKey: SectionKey, optionId: string): void {
    if (sectionKey !== 'meal' && this.isNoMealSelected) {
      return;
    }
    if (sectionKey === "meal") {
      this.selectMeal(optionId as MealOptionState);
    }
    if (sectionKey === "drink") {
      this.selectDrink(optionId as DrinkOptionState);
    }
    if (sectionKey === "transport") {
      this.selectTransport(optionId as TransportOptionState);
    }
  }

  isSelected(sectionKey: SectionKey, optionId: string): boolean {
    if (sectionKey === "meal") {
      return this.mealSelection === optionId;
    }
    if (sectionKey === "drink") {
      return this.drinkSelection === optionId;
    }
    return this.transportSelection === optionId;
  }

  private isMealOption(option: Option): option is MealOption {
    return "dietIcon" in option || option.id === "Hot" || option.id === "Cold" || option.id === "No Meal";
  }

  hasWaitlist(option: Option): option is MealOption {
    return this.isMealOption(option) && option.id !== 'No Meal' && option.waitlistCount > 0;
  }

  hasDietDetails(option: Option): option is MealOption & { dietIcon: string; dietLabel: string } {
    return this.isMealOption(option) && !!option.dietIcon && !!option.dietLabel;
  }

  trackByOptionId(_: number, option: Option): string {
    return option.id;
  }

  hasAnySelection(): boolean {
    return (
      this.mealSelection !== null ||
      this.drinkSelection !== null ||
      (this.canShowTransport && this.transportSelection !== null)
    );
  }

  hasAnyWaitlist(): boolean {
    return this.mealOptions.some(opt => opt.waitlistCount > 0);
  }

  onBack(): void {
    handleBackNavigation(this.router, this.dateSelectionService);
  }

  onApplyForFuture(): void {
    if (!this.areRequiredPreferencesSelected()) {
      this.toasterMessage = this.missingPreferencesMessage();
      this.toasterVariant = "warning";
      return;
    }

    const selectedDateStr = this.date ?? generateDateOptions(4)[0]?.dateValue;
    if (!selectedDateStr) {
      this.toasterMessage = "Date is not available";
      this.toasterVariant = "warning";
      return;
    }

    // Same date window as scheduled-confirmed (today + 3 weekdays), excluding today.
    const scheduleOptions = generateDateOptions();
    const todayValue = scheduleOptions[0]?.dateValue;
    const futureSlots = scheduleOptions.filter(
      (option) => option.dateValue && option.dateValue !== todayValue
    );
    if (futureSlots.length === 0) {
      this.toasterMessage = "Upcoming future slots are not yet Open!";
      this.toasterVariant = "warning";
      return;
    }

    // Store current preferences and let the user pick which future dates to apply.
    sessionStorage.setItem(
      "applyFuturePreferences",
      JSON.stringify({
        sourceDate: selectedDateStr,
        mealPreferences: this.buildMealPreferencesPayload(),
        userId: this.userDetails.value,
        programId: getProgramId(),
      })
    );
    sessionStorage.setItem("previousRoute", "/meal-preferance");
    this.router.navigate(["/apply-future-dates"]);
  }

  onSave(): void {
    if (!this.date) {
      this.toasterMessage = "Date is not available";
      this.toasterVariant = "warning";
      return;
    }

    // Edit + untab all: empty preferences deletes the meal row (cancel reservation).
    if (this.isCancelingExistingReservation()) {
      this.updateMealData(this.buildCancelPayload());
      return;
    }

    if (!this.areRequiredPreferencesSelected()) {
      this.toasterMessage = this.missingPreferencesMessage();
      this.toasterVariant = "warning";
      return;
    }

    this.updateMealData(null);
  }

  /** Existing booking cleared by untabbing meal — treat as cancel, not incomplete form. */
  private isCancelingExistingReservation(): boolean {
    return this.hadExistingReservation && !this.mealSelection;
  }

  private buildCancelPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      programId: JSON.parse(sessionStorage.getItem('programId') ?? 'null'),
      userId: this.userDetails.value,
      mealDate: this.date,
      mealPreferences: [],
    };
    const location = getSelectedLocation();
    if (location) {
      payload['locationId'] = location.id;
      payload['locationName'] = location.locationName;
    }
    return payload;
  }
  ngAfterViewInit(): void {
    // Trigger animation immediately after view init
    // Use a small delay to ensure Angular has processed the initial state
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
  }
  ngOnInit(): void {
    this.languageSub = this.languageService.language$.subscribe(() => {
      this.rebuildOptionSections();
      this.cdr.markForCheck();
    });
    this.rebuildOptionSections();
    // Initialize date from route params or session storage
    this.initializeDate();
    
    // Subscribe to route params to update date when it changes
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      const newDate = params['date'] ?? sessionStorage.getItem('dateValue') ?? null;
      if (newDate !== this.date) {
        this.date = newDate;
        this.dateLabel = this.buildDateLabel(this.date);
        // Reset selections when date changes
        this.mealSelection = null;
        this.drinkSelection = null;
        this.transportSelection = null;
        this.hadExistingReservation = false;
        // Refetch data for the new date
        this.getMealData();
        this.fetchUserCount();
      }
    });
  }

  private initializeDate(): void {
    this.date = this.route.snapshot.queryParams['date'] ?? sessionStorage.getItem('dateValue') ?? null;
    this.dateLabel = this.buildDateLabel(this.date);
    // Call both APIs initially
    this.getMealData(); // This will also fetch and update the count
    this.fetchUserCount(); // Fetch users/count API
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  getMealData(): void {
    if (!this.date) {
      return;
    }
    
    getMealDataCommon(
      this.mealService,
      this.userDetails.value,
      this.date,
      (response: any) => {
        this.mealData = response;
        this.isTransportationApproved =
          getProgramId() === 1000
            ? response?.isTransportationApproved === true
            : true;
        const approvedRaw =
          typeof response?.transportationOption === 'string'
            ? response.transportationOption
            : typeof response?.TransportationOption === 'string'
              ? response.TransportationOption
              : null;
        this.approvedTransportationOption = approvedRaw?.trim() || null;
        this.applyMealOfferOptions(response);
        this.rebuildOptionSections();
        if (!this.canShowTransport) {
          this.transportSelection = null;
        } else if (
          this.transportSelection &&
          !this.getAllowedTransportOptionIds().includes(this.transportSelection)
        ) {
          // Clear selection if it is no longer allowed for the approved direction.
          this.transportSelection = null;
        }
        this.mealLocationName = this.mealData?.results?.[0]?.locationName ?? '';
        this.hadExistingReservation = false;
        if (this.mealData?.results?.[0]?.mealPreference) {
          for (const meal of this.mealData.results[0].mealPreference) {
            if (meal.preference) {
              const pref = String(meal.preference).trim();
              this.mealSelection =
                pref.toLowerCase() === 'no meal' ? 'No Meal' : (pref as MealOptionState);
            }
            if (meal.drinks) {
              this.drinkSelection = meal.drinks;
            }
            if (meal.transport && this.canShowTransport) {
              this.transportSelection = this.normalizeTransportOption(meal.transport);
            }
          }
          if (this.mealSelection === 'No Meal') {
            this.drinkSelection = null;
            this.transportSelection = null;
          }
          // Any saved meal choice means this screen is editing an existing reservation.
          this.hadExistingReservation = !!this.mealSelection;
          this.applyMealOfferOptions(response);
          this.rebuildOptionSections();
        }
        // Update count from API response
        this.updateCountFromMealData(response);
        // Update displayed count after setting selections
        this.updateDisplayedCount();
      },
      (error: any) => {
        this.mealData = null;
        // Keep transport hidden for Senior Center until meals/get confirms transport approval
        this.isTransportationApproved = getProgramId() !== 1000;
        this.approvedTransportationOption = null;
        this.offerHotMeal = true;
        this.offerColdMeal = true;
        this.rebuildOptionSections();
        // Update displayed count even on error (will use users/count)
        this.updateDisplayedCount();
      }
    );
  }

  /**
   * Fetch meal count from meals/get API after selection
   */
  fetchMealCount(): void {
    if (!this.date) {
      return;
    }
    
    getMealDataCommon(
      this.mealService,
      this.userDetails.value,
      this.date,
      (response: any) => {
        this.updateCountFromMealData(response);
      },
      () => {
        // Error fetching meal count
      }
    );
  }

  /**
   * Update waitlist counts from meals/get API response
   */
  updateCountFromMealData(response: any): void {
    if (response?.results && response.results.length > 0) {
      const result = response.results[0];
      this.mealCountData = result;
    }
    // Update displayed count and waitlist counts
    this.updateDisplayedCount();
  }

  /**
   * Fetch user count from users/count API
   */
  fetchUserCount(): void {
    if (!this.date) {
      return;
    }
    
    const location = getSelectedLocation();
    const payload: any = {
      programId: JSON.parse(sessionStorage.getItem('programId') ?? 'null'),
      date: [this.date],
      ...(location ? { locationId: location.id } : {})
    };

    this.userService.getUsersCount(payload).subscribe({
      next: (response: any) => {
        this.userCountData = response;
        // Update displayed count and waitlist counts
        this.updateDisplayedCount();
      },
      error: () => {
        // Error fetching user count
      }
    });
  }

  /**
   * Get meals count from users/count API response
   */
  private getMealsCountFromUserData(): number {
    if (this.userCountData?.data && this.userCountData.data.length > 0) {
      const dateData = this.userCountData.data.find((entry: any) => entry.date === this.date);
      if (dateData?.counts) {
        // Try different possible paths for meals count
        return dateData.counts.meals ?? 
               dateData.counts.mealsCount ?? 
               dateData.counts.mealCount ??
               dateData.counts.pending ?? 
               dateData.counts.total ??
               0;
      }
    }
    return 0;
  }

  /**
   * Update displayed count based on selection state
   * - If meal is selected: selected option shows count from meals/get API, other options show count from users/count API
   * - If no meal selected: all options show count from users/count API
   * Also updates waitlist counts on meal options
   */
  updateDisplayedCount(): void {
    const userCount = this.getMealsCountFromUserData();
    
    if (this.mealSelection) {
      // Use count from meals/get API when meal is selected
      this.displayedCount = this.mealCountData?.count ?? 0;
      
      // Update waitlist count for selected meal option (from meals/get)
      const selectedMealOption = this.mealOptions.find(opt => opt.id === this.mealSelection);
      if (selectedMealOption) {
        selectedMealOption.waitlistCount = this.displayedCount;
      }
      
      // Update other meal options with count from users/count API
      this.mealOptions.forEach(option => {
        if (option.id !== this.mealSelection) {
          option.waitlistCount = userCount;
        }
      });
    } else {
      // Use count from users/count API when no meal is selected
      this.displayedCount = userCount;
      
      // Update waitlist count for all meal options when no meal is selected
      this.mealOptions.forEach(option => {
        option.waitlistCount = this.displayedCount;
      });
    }
  }

  private buildDateLabel(dateValue: string | null): string {
    const targetDate = this.parseDateValue(dateValue);
    const today = this.normalizeToMidnight(new Date());
    const target = this.normalizeToMidnight(targetDate);
    const dayDifference = target.getTime() - today.getTime();
    let prefix = 'Upcoming';

    if (dayDifference === 0) {
      prefix = 'Today';
    } else if (dayDifference === 24 * 60 * 60 * 1000) {
      prefix = 'Tomorrow';
    }

    const weekday = targetDate.toLocaleDateString('en-US', { weekday: 'long' });
    const month = targetDate.toLocaleDateString('en-US', { month: 'long' });
    const dayOfMonth = targetDate.getDate();

    return `${prefix} - ${weekday} ${dayOfMonth}, ${month}`;
  }

  private parseDateValue(dateValue: string | null): Date {
    if (!dateValue) {
      return new Date();
    }

    const normalized = dateValue.includes('T') ? dateValue : `${dateValue}T00:00:00`;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private normalizeToMidnight(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  updateMealData(Payload: any): void {
    const payload: any = Payload ?? {
      programId: JSON.parse(sessionStorage.getItem('programId') ?? 'null'),
      userId: this.userDetails.value,
      mealDate: this.date,
      mealPreferences: this.buildMealPreferencesPayload(),
    };

    const location = getSelectedLocation();
    if (location) {
      payload['locationId'] = location.id;
      payload['locationName'] = location.locationName;
    }

    const isCancel =
      Array.isArray(payload.mealPreferences) && payload.mealPreferences.length === 0;

    this.mealService.updateMealInfo(payload).subscribe({
      next: (response : any) => {
        this.toasterMessage = isCancel
          ? "Reservation cancelled"
          : "Meal info updated successfully";
        this.toasterVariant = "success";
        if (isCancel) {
          this.hadExistingReservation = false;
        }
        const previousRoute = (sessionStorage.getItem('previousRoute') || '').trim();
        const editFromSuccess = sessionStorage.getItem('editFromSuccess') === 'true';

        // Keep the meal date as the activity booking day (future prebook must not fall back to today).
        const mealDate = (this.date || sessionStorage.getItem('dateValue') || '').trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(mealDate)) {
          sessionStorage.setItem('dateValue', mealDate);
        }

        // Cancel: return to the schedule/done screen — do not push into activities.
        if (isCancel) {
          if (editFromSuccess) {
            sessionStorage.removeItem('editFromSuccess');
          }
          const backTo =
            previousRoute === '/success' || previousRoute === '/success-meal-transport'
              ? previousRoute
              : '/success-meal-transport';
          sessionStorage.removeItem('previousRoute');
          this.router.navigate([backTo]);
          return;
        }

        // Done-screen Edit / prebook date card: meal → activities for that same day → return.
        // (Previously returned to success-meal-transport and skipped activities, so 12/14 showed None.)
        if (
          editFromSuccess ||
          previousRoute === '/success' ||
          previousRoute === '/success-meal-transport'
        ) {
          if (editFromSuccess) {
            sessionStorage.removeItem('editFromSuccess');
          }
          sessionStorage.setItem(
            'previousRoute',
            previousRoute === '/success' || previousRoute === '/success-meal-transport'
              ? previousRoute
              : '/success',
          );
          this.router.navigate(['/activity-screen']);
          return;
        }

        sessionStorage.removeItem('previousRoute');
        this.router.navigate(['/scheduled-confirmed']);
      },
      error: (error : any) => {
        this.toasterMessage = error.error?.message ?? 'Error updating meal info';
        this.toasterVariant = "danger";
      }
    });
  }

  private areRequiredPreferencesSelected(): boolean {
    if (!this.mealSelection) {
      return false;
    }
    if (this.mealSelection === 'No Meal') {
      return true;
    }
    if (!this.drinkSelection) {
      return false;
    }
    if (this.canShowTransport && !this.transportSelection) {
      return false;
    }
    return true;
  }

  private missingPreferencesMessage(): string {
    if (this.isNoMealSelected) {
      return 'Please confirm No Meal or select a meal preference';
    }
    return this.canShowTransport
      ? 'Please select all preferences (Meal, Drink, and Transportation)'
      : 'Please select all preferences (Meal and Drink)';
  }

  private buildMealPreferencesPayload(): Array<Record<string, string | null>> {
    if (this.mealSelection === 'No Meal') {
      return [
        { preference: 'No Meal' },
        { drinks: null },
        { transport: 'Not Required' },
      ];
    }
    const preferences: Array<Record<string, string | null>> = [
      { preference: this.mealSelection },
      { drinks: this.drinkSelection },
    ];
    if (this.canShowTransport && this.transportSelection) {
      preferences.push({ transport: this.transportSelection });
    }
    return preferences;
  }
}
